import { createEnum } from '../helpers'
import {
  MATERIAL_MARKING_CHANNELS,
  MaterialMarkingChannel,
  type MaterialDistributionChannel,
} from '../materials'

// FIXME: Update codebase to stop using CHANNEL_VALUES instead of MATERIAL_MARKING_CHANNELS
export const CHANNEL_VALUES = MATERIAL_MARKING_CHANNELS
export const CHANNELS = createEnum(MATERIAL_MARKING_CHANNELS)
// FIXME: Update codebase to stop using Channel instead of MaterialMarkingChannel
export type Channel = MaterialMarkingChannel

export const MATERIAL_DISTRIBUTION_CHANNEL_TO_CHANNELS: Record<
  MaterialDistributionChannel,
  MaterialMarkingChannel[]
> = {
  domestic: ['DSS', 'MW', 'WH'],
  export: ['CDF'],
}

export const CHANNEL_MIX_IMPORT_ERRORS = [
  'invalidProvince',
  'invalidDistributionChannel',
  'zodSchemaError',
] as const

export const CHANNEL_MIX_IMPORT_ERROR_TYPES = createEnum(CHANNEL_MIX_IMPORT_ERRORS)
export type ChannelMixImportErrorType = (typeof CHANNEL_MIX_IMPORT_ERRORS)[number]

export const CHANNEL_MIX_CREATE_ERRORS = ['crtPeriodConflict'] as const

export const CHANNEL_MIX_CREATE_ERROR_TYPES = createEnum(CHANNEL_MIX_CREATE_ERRORS)
export type ChannelMixCreateErrorType = (typeof CHANNEL_MIX_CREATE_ERRORS)[number]

export const CHANNEL_MIX_CREATE_CONSTRAINT_TO_ERROR: Record<string, ChannelMixCreateErrorType> = {
  channel_mix_crt_period_unique: CHANNEL_MIX_CREATE_ERROR_TYPES.crtPeriodConflict,
}

/** Row-level failures of the unreleased materials CSV import (`import-unreleased-materials`). */
export const UNRELEASED_MATERIAL_IMPORT_ERRORS = [
  'zodSchemaError',
  'emptyMaterial',
  'unknownMaterial',
  'emptyRow',
  'invalidMaterialGroup',
  'materialGroupMismatch',
  'invalidBrandHouseCode',
  'invalidProductType',
  'invalidPriceSegment',
  'invalidMarking',
] as const

export const UNRELEASED_MATERIAL_IMPORT_ERROR_TYPES = createEnum(UNRELEASED_MATERIAL_IMPORT_ERRORS)
export type UnreleasedMaterialImportErrorType = (typeof UNRELEASED_MATERIAL_IMPORT_ERRORS)[number]

export const UNRELEASED_MATERIAL_SOURCE_TYPES = [
  'volumesChannelContributions',
  'volumesNcProvincialContributions',
  'costOfGoods',
  'bpcActuals',
] as const

export type UnreleasedMaterialSourceType = (typeof UNRELEASED_MATERIAL_SOURCE_TYPES)[number]

/**
 * One import that carries an unreleased SKU. Volumes and Cost of Goods imports are monthly and
 * addressed by their `YYYY-MM-01` period; BPC Actuals imports are yearly and addressed by their year.
 */
export type UnreleasedMaterialSourceImport =
  | {
      type: Exclude<UnreleasedMaterialSourceType, 'bpcActuals'>
      id: number
      name: string
      period: string
    }
  | { type: 'bpcActuals'; id: number; name: string; year: number }

export const UNRELEASED_MATERIAL_SOURCE_TYPE_LABEL_KEYS: Record<
  UnreleasedMaterialSourceType,
  string
> = {
  volumesChannelContributions: 'route.crt.channelContributions',
  volumesNcProvincialContributions: 'route.crt.ncProvincialContributions',
  costOfGoods: 'route.crt.costOfGoods',
  bpcActuals: 'route.crt.bpcActuals',
}

export const PRODUCT_TYPE_VARIANT_IMPORT_ERRORS = ['zodSchemaError', 'emptyRequiredField'] as const

export const PRODUCT_TYPE_VARIANT_IMPORT_ERROR_TYPES = createEnum(
  PRODUCT_TYPE_VARIANT_IMPORT_ERRORS,
)
export type ProductTypeVariantImportErrorType = (typeof PRODUCT_TYPE_VARIANT_IMPORT_ERRORS)[number]

export const PRODUCT_CATEGORIES = ['FMC', 'VAPOUR', 'NRT'] as const
export const PRODUCT_CATEGORY = createEnum(PRODUCT_CATEGORIES)
export type ProductCategory = (typeof PRODUCT_CATEGORIES)[number]

/** Categories a product type can belong to. FMC groups have no product type. */
export const PRODUCT_TYPE_CATEGORIES: ProductCategory[] = ['VAPOUR', 'NRT']

/**
 * The columns an unreleased material must carry before CRT treats it as mapped. `categories`
 * restricts a rule to the material's product category (derived from its material group); a rule
 * without one applies to every material. Brand is derived from the brand house code, prices and
 * variant are optional.
 *
 * The SQL twin is `incompleteUnreleasedMaterialScope` in `@repo/crt-kpis`; keep them in step.
 */
export const UNRELEASED_MATERIAL_REQUIRED_FIELDS = [
  { field: 'materialGroupCode' },
  { field: 'brandCode' },
  { field: 'marking' },
  { field: 'priceSegment' },
  { field: 'productType', categories: PRODUCT_TYPE_CATEGORIES },
  { field: 'packContentSize', categories: ['FMC'] as ProductCategory[] },
] as const satisfies ReadonlyArray<{ field: string; categories?: ProductCategory[] }>

export type UnreleasedMaterialRequiredField =
  (typeof UNRELEASED_MATERIAL_REQUIRED_FIELDS)[number]['field']

/** The required-field rule for a column, or `undefined` when the column is optional. */
export const getUnreleasedMaterialRequiredRule = (field: string) =>
  UNRELEASED_MATERIAL_REQUIRED_FIELDS.find((rule) => rule.field === field)

/**
 * The required fields an unreleased material is still missing, given the product category of its
 * material group (`null` when it has no group or the group is unknown, in which case only the
 * unconditional rules apply — the same reading as the SQL twin). Empty when the material is valid.
 */
export function getMissingUnreleasedMaterialFields(
  material: Partial<Record<UnreleasedMaterialRequiredField, string | null | undefined>>,
  productCategory: ProductCategory | null | undefined,
): UnreleasedMaterialRequiredField[] {
  return UNRELEASED_MATERIAL_REQUIRED_FIELDS.filter((rule) => {
    const applies =
      !('categories' in rule) ||
      (productCategory != null && rule.categories.includes(productCategory))
    return applies && !material[rule.field]
  }).map((rule) => rule.field)
}

export const PRODUCT_CATEGORY_LABEL_KEYS: Record<ProductCategory, string> = {
  FMC: 'productCategory.fmc',
  VAPOUR: 'productCategory.vapour',
  NRT: 'productCategory.nrt',
}

/**
 * The attribute a Volume Scenario target keys off, one per product category. Kept as an
 * `(attributeKey, attributeValue)` pair so adding a category needs no schema change — extend this
 * list and the `PRODUCT_CATEGORY_TO_ATTRIBUTE` map below.
 */
export const TARGET_ATTRIBUTE_KEYS = ['packContentSize', 'productType', 'priceSegment'] as const
export type TargetAttributeKey = (typeof TARGET_ATTRIBUTE_KEYS)[number]

export const PRODUCT_CATEGORY_TO_ATTRIBUTE: Record<ProductCategory, TargetAttributeKey> = {
  NRT: 'packContentSize',
  VAPOUR: 'productType',
  FMC: 'priceSegment',
}

/**
 * SAP price-segment codes, as stored in `fz_material.price_segment` (and surfaced by
 * `materials_hydrated_mview.price_segment`). The companion `price_segment_nm` column is NOT a usable
 * label: its casing varies for the same segment (`'Premium segment'` / `'Premium Segment'`,
 * `'Value for Money seg'` / `'Value for Money Seg'`) and a handful of materials carry a name that
 * contradicts their code. The code is the reliable field, so it is what we key off.
 */
export const PRICE_SEGMENT_CODES = ['01', '02', '03'] as const
export type PriceSegmentCode = (typeof PRICE_SEGMENT_CODES)[number]

export const PRICE_SEGMENT_LABEL_KEYS: Record<PriceSegmentCode, string> = {
  '01': 'pricingSegment.premium',
  '02': 'pricingSegment.valueForMoney',
  '03': 'pricingSegment.low',
}

/**
 * The price segment as written in CSV files (unreleased materials export/import). A wire format, not
 * UI text: the UI translates `PRICE_SEGMENT_LABEL_KEYS`.
 */
export const PRICE_SEGMENT_CSV_NAMES: Record<PriceSegmentCode, string> = {
  '01': 'Premium',
  '02': 'Value for Money',
  '03': 'Low',
}

/** Normalised (trimmed, single-spaced, lower-cased) CSV spellings of a price segment → its code. */
const PRICE_SEGMENT_CSV_ALIASES: Record<string, PriceSegmentCode> = {
  '01': '01',
  '1': '01',
  premium: '01',
  '02': '02',
  '2': '02',
  'value for money': '02',
  vfm: '02',
  '03': '03',
  '3': '03',
  low: '03',
}

/** Collapses whitespace and case so `' Value  For Money '` and `'VFM'` both resolve. */
export const normalizeCsvValue = (value: string): string =>
  value.trim().replace(/\s+/g, ' ').toLowerCase()

/** Resolves a CSV price segment cell (`Premium`, `VFM`, `02`, `2`, ...) to its code, or `undefined`. */
export const getPriceSegmentCodeFromCsvValue = (raw: string): PriceSegmentCode | undefined =>
  PRICE_SEGMENT_CSV_ALIASES[normalizeCsvValue(raw)]

export const BRAND_HOUSE_CODE_TO_LABEL: Record<string, string> = {
  AVAN: 'Avanti',
  BLUE: 'Blues',
  BOND: 'Bond Street',
  CAME: 'Cameo',
  CAPR: 'Capri',
  CHES: 'Chesterfield',
  CHVR: 'Chevron',
  CLEA: 'Choice Leaf',
  CNGL: 'Canadian Gold',
  DUMA: 'du Maurier',
  DUNH: 'Dunhill',
  ECLI: 'Eclipse',
  EMBA: 'Embassy',
  GLO: 'glo',
  ICE: 'Ice',
  JPPS: 'John Player Plus',
  JPS: 'John Player Special',
  JPST: 'John Player ST',
  JPYR: 'John Player',
  KENT: 'Kent',
  KOOL: 'Kool',
  LAMB: 'Lambert & Butler',
  LUCK: 'Lucky Strike',
  LUCS: 'Luckies',
  LUXE: 'Luxe',
  MARL: 'Marlboro',
  MATI: 'Matinée',
  MEDA: 'Medallion',
  NEO: 'Neo',
  NEWP: 'Newport',
  NOBH: 'No Brand House',
  PALL: 'Pall Mall',
  PEJA: 'Peter Jackson',
  PLAY: "Player's",
  PRIN: 'Prince',
  SMWN: 'Smokers Own',
  STER: 'Sterling',
  SWCA: 'Sweet Caporal',
  TBKA: 'Taboka',
  TRUE: 'TRUE',
  VELO: 'Velo',
  VICE: 'Viceroy',
  VLPK: 'Valu-Pak',
  VOGU: 'Vogue',
  VUSE: 'Vuse',
  VYPE: 'Vype',
  ZIGZ: 'Zig Zag',
  ZONN: 'Zonnic',
}

export function getBrandHouseLabelFromCode(brandHouseCode: string): string | undefined {
  return BRAND_HOUSE_CODE_TO_LABEL[brandHouseCode]
}

/** Lower-cased brand house label → code, the inverse of {@link BRAND_HOUSE_CODE_TO_LABEL}. */
const BRAND_HOUSE_LABEL_TO_CODE: Record<string, string> = Object.fromEntries(
  Object.entries(BRAND_HOUSE_CODE_TO_LABEL).map(([code, label]) => [
    normalizeCsvValue(label),
    code,
  ]),
)

/** Resolves a brand house label (`'Vuse'`, `'du maurier'`) to its code, or `undefined`. */
export function getBrandHouseCodeFromLabel(label: string): string | undefined {
  return BRAND_HOUSE_LABEL_TO_CODE[normalizeCsvValue(label)]
}
